import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Trash2, Phone, Mail, MessageSquare } from "lucide-react";

const statusColors = {
  new: "bg-brand-gold text-black",
  read: "bg-blue-100 text-blue-700",
  responded: "bg-green-100 text-green-700",
};

export default function AdminEnquiries() {
  const qc = useQueryClient();
  const { data: enquiries = [], isLoading } = useQuery({
    queryKey: ["admin-enquiries"],
    queryFn: () => api.get("/admin/enquiries").then((r) => r.data),
  });

  const setStatus = async (id, status) => {
    try {
      await api.put(`/admin/enquiries/${id}`, { status });
      qc.invalidateQueries({ queryKey: ["admin-enquiries"] });
      qc.invalidateQueries({ queryKey: ["admin-stats"] });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/enquiries/${id}`);
      toast.success("Enquiry deleted");
      qc.invalidateQueries({ queryKey: ["admin-enquiries"] });
      qc.invalidateQueries({ queryKey: ["admin-stats"] });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-enquiries">
      <div className="mb-6">
        <h1 className="font-display font-black text-3xl text-slate-900">Enquiries</h1>
        <p className="text-slate-500">{enquiries.length} received</p>
      </div>

      {isLoading ? (
        <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-brand-blue" /></div>
      ) : enquiries.length === 0 ? (
        <div className="py-20 text-center border-2 border-dashed rounded-xl text-slate-400" data-testid="no-enquiries">
          <MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-40" />
          No enquiries yet.
        </div>
      ) : (
        <div className="space-y-4">
          {enquiries.map((e) => (
            <div key={e.id} className="bg-white border rounded-xl p-5" data-testid={`enquiry-${e.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-display font-bold text-slate-900">{e.name}</h3>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${statusColors[e.status] || statusColors.new}`}>{e.status}</span>
                  </div>
                  <div className="flex flex-wrap gap-4 mt-1 text-sm text-slate-500">
                    <a href={`tel:${e.phone}`} className="inline-flex items-center gap-1 hover:text-brand-blue"><Phone className="w-3.5 h-3.5" /> {e.phone}</a>
                    {e.email && <a href={`mailto:${e.email}`} className="inline-flex items-center gap-1 hover:text-brand-blue"><Mail className="w-3.5 h-3.5" /> {e.email}</a>}
                  </div>
                </div>
                <span className="text-xs text-slate-400">{new Date(e.created_at).toLocaleString()}</span>
              </div>
              {e.subject && <p className="mt-3 font-semibold text-slate-700">{e.subject}</p>}
              <p className="mt-1 text-slate-600 whitespace-pre-wrap">{e.message}</p>
              <div className="flex flex-wrap items-center gap-2 mt-4">
                <select value={e.status} onChange={(ev) => setStatus(e.id, ev.target.value)} data-testid={`enquiry-status-${e.id}`} className="text-sm border rounded-lg px-3 py-1.5 bg-white">
                  <option value="new">New</option>
                  <option value="read">Read</option>
                  <option value="responded">Responded</option>
                </select>
                <button onClick={() => remove(e.id)} data-testid={`delete-enquiry-${e.id}`} className="inline-flex items-center gap-1 text-sm text-red-600 border rounded-lg px-3 py-1.5 hover:bg-red-50"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
